import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoCheckbox, RobotoTypography } from "@/shared/components";
import { FileVizLink } from "@/shared/components/links";
import { TopicSequence } from "@/shared/domain/topics/TopicSequence";
import { nanoSecToDate, nanoSecToSecLossy } from "@/shared/time";

interface TopicSequenceExpanderDetailsProps {
  topicSequence: TopicSequence;
  /** Set of selected file IDs from the Dataset Topics tab. */
  selectedFileIds: Set<string>;
  /** Callback invoked when user checks/unchecks a file checkbox. */
  onFileSelectionChange: (fileId: string, checked: boolean) => void;
}

export const TopicSequenceDetails: React.FC<
  TopicSequenceExpanderDetailsProps
> = ({ topicSequence, selectedFileIds, onFileSelectionChange }) => {
  const theme = useTheme();

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <List>
            <ListItem>
              <ListItemText
                primary={"Start Time"}
                primaryTypographyProps={{ variant: "subtitle2" }}
                secondary={
                  <>
                    {topicSequence.start_time !== null &&
                    topicSequence.start_time !== undefined ? (
                      <>
                        {nanoSecToDate(topicSequence.start_time).toLocaleString(
                          undefined,
                          { timeZoneName: "short" },
                        )}
                        <br />
                        &rArr; {nanoSecToSecLossy(
                          topicSequence.start_time,
                        )}{" "}
                        seconds since epoch
                      </>
                    ) : (
                      "N/A"
                    )}
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"End Time"}
                primaryTypographyProps={{ variant: "subtitle2" }}
                secondary={
                  <>
                    {topicSequence.end_time !== null &&
                    topicSequence.end_time !== undefined ? (
                      <>
                        {nanoSecToDate(topicSequence.end_time).toLocaleString(
                          undefined,
                          { timeZoneName: "short" },
                        )}
                        <br />
                        &rArr; {nanoSecToSecLossy(topicSequence.end_time)}{" "}
                        seconds since epoch
                      </>
                    ) : (
                      "N/A"
                    )}
                  </>
                }
              />
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <List>
            <ListItem>
              <ListItemText
                primary="Associated Files"
                primaryTypographyProps={{ variant: "subtitle2" }}
                secondary={
                  <List dense>
                    {topicSequence.topics.map((topic) => {
                      const fileId = topic.association.association_id;
                      const isChecked = selectedFileIds.has(fileId);
                      return (
                        <ListItem
                          key={topic.topic_id}
                          dense
                          sx={{ paddingLeft: 0 }}
                        >
                          <RobotoCheckbox
                            sx={{
                              ml: theme.spacing(0),
                              mr: theme.spacing(1),
                              pl: 0,
                            }}
                            checked={isChecked}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              onFileSelectionChange(fileId, e.target.checked);
                            }}
                            color="default"
                            inputProps={{ "aria-label": "select file" }}
                          />
                          <ListItemText
                            primary={
                              <FileVizLink
                                fileId={fileId}
                                subtitle={
                                  topic.start_time !== null &&
                                  topic.start_time !== undefined &&
                                  topic.end_time !== null &&
                                  topic.end_time !== undefined && (
                                    <RobotoTypography
                                      variant="body2"
                                      sx={{
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      {nanoSecToSecLossy(
                                        topic.end_time - topic.start_time,
                                      ).toFixed(3)}
                                      s from{" "}
                                      {nanoSecToDate(
                                        topic.start_time,
                                      ).toLocaleString(undefined, {
                                        timeZoneName: "short",
                                      })}
                                    </RobotoTypography>
                                  )
                                }
                              />
                            }
                            primaryTypographyProps={{ component: "span" }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                }
                secondaryTypographyProps={{ component: "span" }}
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </Box>
  );
};
